import { NgClass, NgFor, NgIf, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Params, Router, RouterLink } from '@angular/router';

import { ImageSizes, LinkComponent, SvgComponent } from '@app/base-components';
import { ButtonQaId, HabaQaId } from '@app/helpers';
import { OciService } from '@app/services';
import { Entry } from 'contentful';
import {
  NavigationNode,
  NavigationQuickCall,
} from 'haba-contentful-components/lib/interfaces/navigation';
import { I18nModule, UrlModule } from 'haba-core';
import { UserAccountFacade } from 'haba-featurelibs/user/account/root';
import { TopLinksCountrySelectionComponent } from '../../navigation/top-links-country-selection/top-links-country-selection.component';
import { RemoveDollarSignPipe } from '../horizontal-menu/removeDollarSign.pipe';
import { filter, startWith, Subject, take, takeUntil } from 'rxjs';
import { Category, FilterCategory, PrefixboxFilterService, PrefixboxService } from 'haba-emporix';
import { ImageComponent } from 'src/app/product/product-card-base/image/image.component';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  imports: [
    NgFor,
    NgIf,
    NgClass,
    SvgComponent,
    RouterLink,
    LinkComponent,
    TopLinksCountrySelectionComponent,
    UrlModule,
    I18nModule,
    RemoveDollarSignPipe,
    UpperCasePipe,
    NgTemplateOutlet,
    ImageComponent,
  ],
})
export class SideMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Output() requestScrollTop = new EventEmitter<void>();
  @Output() closeSearch = new EventEmitter<void>();

  @Input() navigationNodes: Entry<NavigationNode>[];
  @Input() quickLinks: Entry<NavigationQuickCall>[];

  public imageSizes = ImageSizes;

  headerNavigationLink: ButtonQaId;
  baseId: HabaQaId;
  toggle = {};

  showLevel1 = true;
  showLevel2 = true;
  showShopMenu = false;

  categoryTree: Category[];
  categories: Category[];
  currentCategory: Category;
  lvl1CategoryNode: Entry<NavigationNode>;
  parent: Category;

  private onDestroy$: Subject<any> = new Subject();

  constructor(
    protected userAccountFacade: UserAccountFacade,
    protected ociService: OciService,
    private router: Router,
    private prefixboxService: PrefixboxService,
    private filterService: PrefixboxFilterService
  ) {}

  ngOnInit() {
    this.initIds();

    this.router.events
      .pipe(
        takeUntil(this.onDestroy$),
        filter((event) => event instanceof NavigationEnd),
        startWith(null)
      )
      .subscribe(() => {
        this.showShopMenu = this.isShopRelatedPage;
      });
  }

  ngOnChanges() {
    if (this.navigationNodes) {
      this.prefixboxService
        .getCategoryTree()
        .pipe(take(1))
        .subscribe((categoryTree) => {
          const shopNode = this.navigationNodes.find(
            (node) => node.fields.name.toLowerCase() === 'shop'
          );
          if (!shopNode) return;

          const shopNodeUrls = shopNode.fields.childNavigationNodes.map((node) => node.fields.url);

          const rawCategoryTree = categoryTree.filter((category) =>
            shopNodeUrls.some((nodeUrl) => nodeUrl.includes(category.code))
          );
          this.categoryTree = this.prefixboxService.removeEmptyCategories(rawCategoryTree);

          const [url, categoryCode] = this.router.url.split('?')[0].split('--');
          const [, pageType] = url.split('/');

          // on category page, we have to init the menu with that specific category
          if (categoryCode && pageType === 'c') {
            let result = this.prefixboxService.findByCode(this.categoryTree, categoryCode);

            if (result && !result.category.subcategories.length) {
              result = this.prefixboxService.findByCode(this.categoryTree, result.parent.code);
            }

            this.currentCategory = result?.category;
            this.parent = result?.parent;

            const topNode = result
              ? shopNode.fields.childNavigationNodes.find(
                  (node) => node.fields.url === result.topCategory.url
                )
              : undefined;

            this.lvl1CategoryNode =
              topNode?.fields?.cloudinaryImage || topNode?.fields?.cloudinaryImage2
                ? topNode
                : shopNode;

            const subcategories = result ? result.category.subcategories : this.categoryTree;
            this.setFilteredCategories(result?.category?.code || categoryCode, subcategories, result?.category?.name);
          } else {
            this.showAllShopCategories();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  toggleLevel1(item: Entry<NavigationNode>, i: number) {
    if (item.fields?.name?.trim()?.toLowerCase() === 'shop') {
      this.showShopMenu = true;
      this.requestScrollTop.emit();
      return;
    }

    this.toggle[i] = !this.toggle[i];
    this.showLevel1 = false;
    this.requestScrollTop.emit();
  }

  toggleLevel2(item: Entry<NavigationNode>, i: number, i2: number) {
    this.toggle[i + '.' + i2] = !this.toggle[i + '.' + i2];
    this.showLevel2 = false;
    this.requestScrollTop.emit();
  }

  closeNavigation() {
    this.closeSearch.emit();
  }

  goToPage(url: string) {
    this.router.navigate([url]).then();
    this.closeNavigation();
  }

  openCategory(code: string) {
    const result = this.prefixboxService.findByCode(this.categoryTree, code);
    this.currentCategory = result.category;
    this.parent = result.parent;

    if (this.currentCategory.level === 1) {
      const shopNode = this.navigationNodes.find(
        (node) => node.fields.name.toLowerCase() === 'shop'
      );
      const currentNode = shopNode.fields.childNavigationNodes.find(
        (node) => node.fields.url === this.currentCategory.url
      );

      this.lvl1CategoryNode = currentNode;
    }

    if (this.currentCategory.subcategories.length) {
      const [urlPath, urlCategoryCode] = this.router.url.split('?')[0].split('--');
      const [, pageType] = urlPath.split('/');
      const isCurrentPageTree =
        urlCategoryCode &&
        pageType === 'c' &&
        this.prefixboxService.findByCode(this.categoryTree, urlCategoryCode)?.topCategory?.url ===
          result.topCategory?.url;

      if (isCurrentPageTree) {
        this.setFilteredCategories(code, this.currentCategory.subcategories, this.currentCategory.name);
      } else {
        this.categories = this.currentCategory.subcategories;
      }
    } else {
      this.goToPage(this.currentCategory.url);
    }

    this.requestScrollTop.emit();
  }

  showAllShopCategories() {
    this.categories = this.categoryTree;
    this.currentCategory = undefined;
    this.parent = undefined;

    const shopNode = this.navigationNodes.find((node) => node.fields.name.toLowerCase() === 'shop');
    this.lvl1CategoryNode = shopNode;
  }

  get isShopRelatedPage() {
    const urlSegments = this.router.url.split('/');
    return (
      urlSegments[1] === 'shop' ||
      urlSegments[1] === 'c' ||
      urlSegments[1] === 'p' ||
      urlSegments[1] === 'cart'
    );
  }

  private setFilteredCategories(categoryCode: string, categories: Category[], currentCategoryName?: string): void {
    const queryParams: Params = this.router.parseUrl(this.router.url).queryParams;
    this.prefixboxService
      .getCategoryProducts(categoryCode, queryParams)
      .pipe(take(1))
      .subscribe((results) => {
        const filterValues = results?.filters?.find((f) => f.name === 'category')?.values || [];
        const filterTree = this.filterService.createCategoryListFilter(filterValues) || [];

        let relevantFilterTree = filterTree;
        if (currentCategoryName) {
          const match = this.findInFilterTree(filterTree, currentCategoryName);
          relevantFilterTree = match?.subCategories || [];
        }

        this.categories = this.filterCategoryTree(categories, relevantFilterTree);
      });
  }

  private findInFilterTree(filterTree: FilterCategory[], name: string): FilterCategory | null {
    for (const fc of filterTree) {
      if (fc.name === name) return fc;
      if (fc.subCategories?.length) {
        const found = this.findInFilterTree(fc.subCategories, name);
        if (found) return found;
      }
    }
    return null;
  }

  private filterCategoryTree(categories: Category[], filterTree: FilterCategory[]): Category[] {
    return categories
      .filter((cat) => cat.count > 0)
      .map((cat) => {
        const match = filterTree.find((fc) => fc.name === cat.name);
        return match ? { ...cat, count: match.count } : { ...cat, count: 0 };
      });
  }

  private initIds() {
    this.baseId = HabaQaId.forPage(HabaQaId.page.header);
    this.headerNavigationLink = this.baseId.getButtonId(
      HabaQaId.elements.button.headerNavigationLink
    );
  }
}
